import React, { useEffect, useState } from 'react';
import { camBasis } from '../services/worldObjects';
import * as THREE from 'three';
import { useWorldStore } from '../state/worldStore';
import { api } from '../services/api';
import { ensureHouseFill, onHouseFill, poolCounts, houseFillItem } from '../services/houseFill';
import { listPostable } from '../services/postable';
import { roomSpace } from '../state/interiorFloor';
import { ROOMS } from '../rooms';
import { adminPanel, AdminClose } from './adminChrome';

// PlacementPanel — the admin placement tool's controls (spec §4). Walk the city,
// look at a wall, tune the surface here, hit Place. The ghost quad itself lives
// in PlacementLayer (in-scene); this panel owns the draft settings + inventory
// list (select / duplicate / delete). Shown only when AdminPanel's tool is
// 'placements' (admins only).

// Content type → default aspect (§3). Aspect stays overridable.
const CONTENT_TYPES = [
  { key: 'featured_track', label: 'Featured track', aspect: '1:1' },
  { key: 'featured_artist', label: 'Featured artist', aspect: '3:4' },
  { key: 'news_broadcast', label: 'News broadcast', aspect: '16:9' },
  { key: 'interview_podcast', label: 'Interview podcast', aspect: '4:3' },
  { key: 'hypeclip', label: 'HypeClip', aspect: '9:16' },
  // The newest posts, in turn; a click shows the caption (houseFill postsPool).
  { key: 'latest_posts', label: 'Latest posts', aspect: '1:1' },
];
const ASPECTS = ['1:1', '16:9', '4:3', '3:4', '9:16'];
// Only these have an EPISODE to run, so only these can be set to 'play'. A
// featured track has no timeline — a "playing" track surface would be a rotation
// with extra steps, and offering the choice would imply otherwise.
const PLAYABLE = ['interview_podcast', 'news_broadcast'];
// How a playing screen earns its sound. Proximity leads because it is the only
// one that works on every surface the platform ships (phone, stereo/VR, remote).
const TRIGGERS = [
  { key: 'proximity', label: 'Walk up' },
  { key: 'always', label: 'Always on' },
  { key: 'hover', label: 'Hover' },
];
// 'water' shows no content — it renders the sea shader across the placement's
// own footprint. Same tool, because placing a pool is the same act as placing a
// surface: click the ground, size the rectangle.
const SURFACES = ['billboard', 'poster_wall', 'marquee', 'storefront', 'jumbotron', 'cinema_screen', 'water'];
const FRAMES = ['bare', 'framed', 'neon', 'led_panel', 'nameplate'];
const TIERS = ['plaza', 'main', 'side', 'interior'];

const wrap = adminPanel({
  position: 'fixed', left: 'max(16px, env(safe-area-inset-left))', top: 'max(16px, env(safe-area-inset-top))',
  zIndex: 52, width: 'min(300px, 84vw)', maxHeight: '84vh', overflowY: 'auto', pointerEvents: 'auto',
  background: 'rgba(14,9,6,0.95)', border: '1px solid rgba(255,178,94,0.45)', borderRadius: 14,
  padding: 12, color: '#ffe9c4', fontFamily: 'system-ui, sans-serif',
  backdropFilter: 'blur(10px)', WebkitBackdropFilter: 'blur(10px)', boxShadow: '0 16px 44px rgba(0,0,0,0.5)',
});
const head = { fontSize: 10.5, letterSpacing: 1.3, textTransform: 'uppercase', opacity: 0.72, margin: '8px 2px 5px' };
const chipRow = { display: 'flex', flexWrap: 'wrap', gap: 4 };
const chip = (on) => ({
  padding: '4px 8px', borderRadius: 999, cursor: 'pointer', font: '600 11.5px system-ui',
  border: `1px solid ${on ? '#ffb25e' : 'rgba(255,255,255,0.16)'}`,
  background: on ? '#ffb25e' : 'transparent', color: on ? '#1c1206' : '#ffe9c4',
});
const input = {
  width: '100%', boxSizing: 'border-box', padding: '7px 9px', borderRadius: 8, fontSize: 13,
  border: '1px solid rgba(255,178,94,0.3)', background: 'rgba(255,255,255,0.06)', color: '#fff', outline: 'none',
};
const btn = (bg = '#ffb25e', c = '#1c1206') => ({
  flex: 1, padding: '9px 0', borderRadius: 9, border: 'none', cursor: 'pointer',
  font: '700 13px system-ui', background: bg, color: c,
});

export default function PlacementPanel() {
  const tool = useWorldStore((s) => s.adminTool);
  const draft = useWorldStore((s) => s.placementDraft);
  const setDraft = useWorldStore((s) => s.setPlacementDraft);
  const placements = useWorldStore((s) => s.placements);
  const upsert = useWorldStore((s) => s.upsertPlacement);
  const removeP = useWorldStore((s) => s.removePlacement);
  const selected = useWorldStore((s) => s.selectedPlacement);
  const setSelected = useWorldStore((s) => s.setSelectedPlacement);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState('');
  // Content URL lives in the shared draft so the selected surface previews it live.
  const contentRef = draft.default_content_ref || '';
  const setContentRef = (v) => setDraft({ default_content_ref: v });
  // House-fill diagnostics — what content is actually available to fill surfaces.
  const [, setPoolTick] = useState(0);
  useEffect(() => {
    ensureHouseFill().then(() => setPoolTick((n) => n + 1));
    return onHouseFill(() => setPoolTick((n) => n + 1));
  }, []);
  const counts = poolCounts();

  const open = tool === 'placements';

  // ── WHICH ROOM AM I PLACING IN ─────────────────────────────────────────────
  // A placement's `zone` is the room it belongs to, or null for the street, and
  // it is stamped from wherever you are standing when you press Place. Getting
  // this wrong is invisible until you walk out — an unscoped surface made indoors
  // reappears hanging over the city — so the panel states the scope rather than
  // leaving it to be inferred.
  const interior = useWorldStore((s) => s.interior);
  const scopeLabel = interior ? (ROOMS[interior]?.name || interior) : 'The street';

  // The room's own designed surfaces, read off the loaded model's affordances.
  // Re-listed whenever the tool opens or the room changes: the model has to be
  // in the scene to be read, and it is not while you are outside.
  const [surfaces, setSurfaces] = useState([]);
  useEffect(() => {
    if (!open || !interior) { setSurfaces([]); return undefined; }
    // The room may still be streaming in when the tool opens.
    let tries = 0;
    const scan = () => {
      const found = listPostable();
      if (found.length || ++tries > 12) { setSurfaces(found); return; }
      timer = setTimeout(scan, 400);
    };
    let timer = null;
    scan();
    return () => clearTimeout(timer);
  }, [open, interior]);

  // Selecting a surface (in-world click or the list) loads ITS settings into the
  // panel, so the controls edit that surface instead of clobbering it with the
  // last-used draft.
  useEffect(() => {
    if (!selected) return;
    const p = placements.find((x) => x.placement_id === selected);
    if (!p) return;
    setDraft({
      aspect: p.aspect, surface_type: p.surface_type, frame_style: p.frame_style,
      traffic_tier: p.traffic_tier, width: p.width, label: p.label || '',
      content_policy: p.content_policy || [],
      // Without this, selecting a PLAYING screen loaded the last-used draft's
      // behaviour and the next Apply would quietly turn it back into a rotating
      // poster — the classic "editing one field reset another" bug.
      playback: p.playback || 'rotate',
      trigger_mode: p.trigger_mode || 'proximity',
      volume: p.volume == null ? 1 : p.volume,
      trigger_radius: p.trigger_radius == null ? 8 : p.trigger_radius,
      default_content_ref: p.default_content_ref || '',
      pos: [...p.pos], quat: [...p.quat],
    });
  }, [selected]); // eslint-disable-line react-hooks/exhaustive-deps

  // Nudge the SELECTED surface without re-aiming at it. Movement is relative to
  // where you are looking — "left" has to mean left from here, or nothing lands
  // where you expect in a city you turn around in.
  const nudge = (axis, dir, fine) => {
    if (!draft.pos) return;
    const step = (fine ? 0.1 : 0.5) * dir;
    const [x, y, z] = draft.pos;
    // Vertical ('height') is untouched by any of this — room space shares world's
    // Y axis. Matched by exclusion, as the original did, so the raise/lower
    // buttons keep working whatever they call themselves.
    if (axis !== 'across' && axis !== 'depth') { setDraft({ pos: [x, +(y + step).toFixed(2), z] }); return; }
    // camBasis is where the CAMERA is looking, in world axes, but draft.pos is
    // room-local for a room-scoped surface. Un-yaw the direction by the room's
    // rotation so "left" still means left from here. Room space shares world's Y
    // axis and only yaws, so one angle is the whole correction — and with rotY 0
    // (every room today) this is the identity.
    const yaw = interior ? (roomSpace.object?.rotation.y || 0) : 0;
    const c = Math.cos(-yaw); const s = Math.sin(-yaw);
    const [wx, wz] = axis === 'across' ? [camBasis.rx, camBasis.rz] : [camBasis.fx, camBasis.fz];
    const dx = wx * c + wz * s;
    const dz = -wx * s + wz * c;
    setDraft({ pos: [+(x + dx * step).toFixed(2), y, +(z + dz * step).toFixed(2)] });
  };
  // Yaw about world Y, composed onto whatever the raycast gave it — so a surface
  // keeps the tilt it was placed with and only spins.
  const turn = (dir, fine) => {
    if (!draft.quat) return;
    const q = new THREE.Quaternion(draft.quat[0], draft.quat[1], draft.quat[2], draft.quat[3]);
    q.premultiply(new THREE.Quaternion().setFromAxisAngle(new THREE.Vector3(0, 1, 0), (fine ? 0.02 : 0.1) * dir));
    setDraft({ quat: [q.x, q.y, q.z, q.w] });
  };

  // Resize the draft surface, clamped to something placeable.
  const resize = (delta) => setDraft({ width: Math.max(0.3, Math.min(60, +(draft.width + delta).toFixed(2))) });

  // Keyboard: -/= resize, Enter places. (Typing in a field is exempt.)
  // SCROLL also resizes while the tool is open — the spec's "scroll or handles".
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.target?.tagName === 'INPUT' || e.target?.tagName === 'TEXTAREA') return;
      if (e.key === '-' || e.key === '=' || e.key === '+') {
        e.preventDefault();
        resize(e.key === '-' ? (e.shiftKey ? -0.15 : -0.5) : (e.shiftKey ? 0.15 : 0.5));
      } else if (e.key === 'Enter') { e.preventDefault(); place(); }
    };
    const onWheel = (e) => {
      if (e.target?.closest?.('[data-placement-panel]')) return; // let the panel scroll
      e.preventDefault();
      resize(e.deltaY < 0 ? (e.shiftKey ? 0.15 : 0.5) : (e.shiftKey ? -0.15 : -0.5));
    };
    window.addEventListener('keydown', onKey);
    window.addEventListener('wheel', onWheel, { passive: false });
    return () => { window.removeEventListener('keydown', onKey); window.removeEventListener('wheel', onWheel); };
  });

  if (!open) return null;

  // ── WORLD → ROOM ───────────────────────────────────────────────────────────
  // The ghost is raycast against the scene, so it is world-space by
  // construction. A room-scoped surface is STORED in the room's space, because a
  // room is a thing that moves: nudge its Y, re-anchor it to another building,
  // turn it, and anything written down in world coordinates stays behind while
  // the walls leave. Converting here means nothing downstream re-applies the
  // room's transform — RoomPlacements renders inside the room's own group, so
  // the group IS the conversion back.
  //
  // The anchor group carries no scale, so this is rigid and `width`/`height` are
  // unchanged. That is deliberate: an ad is sized against the person looking at
  // it, not against the building's fit.
  const toRoomSpace = (t) => {
    const a = roomSpace.object;
    if (!interior || !a) return t; // street, or the room has not mounted yet
    a.updateMatrixWorld(true);
    const pos = a.worldToLocal(new THREE.Vector3(t.pos[0], t.pos[1], t.pos[2]));
    const inv = a.getWorldQuaternion(new THREE.Quaternion()).invert();
    const quat = inv.multiply(new THREE.Quaternion(t.quat[0], t.quat[1], t.quat[2], t.quat[3]));
    return { ...t, pos: [pos.x, pos.y, pos.z], quat: [quat.x, quat.y, quat.z, quat.w] };
  };

  // Create at an explicit transform. Both entry points below go through here so
  // there is exactly one place that decides what a new surface is stamped with —
  // in particular the `zone`, which is the whole reason a room can hold an ad.
  const create = async (world, name) => {
    const t = toRoomSpace(world);
    setBusy(true); setMsg('');
    try {
      const { res, data } = await api.createPlacement({
        label: draft.label || (interior
          ? `${scopeLabel} — ${name || draft.surface_type}`
          : `${draft.surface_type} ${Math.round(t.pos[0])},${Math.round(t.pos[2])}`),
        district: 'hypetown',
        // THE ROOM, or null on the street. PlacementLayer shows a surface only in
        // its own scope, so this is what keeps an indoor ad indoors.
        zone: interior || null,
        // An interior surface is interior traffic by definition; typing it by hand
        // was one more thing to forget.
        traffic_tier: interior ? 'interior' : draft.traffic_tier,
        pos: t.pos, quat: t.quat, width: t.width ?? draft.width, aspect: draft.aspect,
        surface_type: draft.surface_type, frame_style: draft.frame_style,
        content_policy: draft.content_policy,
        playback: draft.playback || 'rotate',
        trigger_mode: draft.trigger_mode || 'proximity',
        volume: draft.volume ?? 1,
        trigger_radius: draft.trigger_radius ?? 8,
        default_content_ref: contentRef || null,
      });
      if (res.ok && data.placement) {
        upsert(data.placement);
        // WHAT WAS ASKED FOR vs WHAT CAME BACK. A surface renders in exactly one
        // scope, and a dropped `zone` is invisible at the moment it happens —
        // the ad simply is not where you put it, and turns up outdoors later.
        // Comparing the two here names the culprit instead of leaving a choice
        // between "the client did not send it" and "the server did not keep it".
        const want = interior || null;
        const got = data.placement.zone || null;
        if (want !== got) {
          console.error(`[placement] ZONE DROPPED — sent "${want}", stored "${got}". `
            + 'The client is scoping correctly, so the row was written without it: the API needs a '
            + 'restart, or `placements` has no `zone` column (migration 251).');
          setMsg(`Placed #${data.placement.placement_id} — but the room was not saved (see console)`);
        } else {
          console.log(`[placement] #${data.placement.placement_id} saved in ${got || 'the street'}`);
          setMsg(`Placed #${data.placement.placement_id} in ${got ? scopeLabel : 'the street'}`);
        }
      } else setMsg(data.error || 'Save failed');
    } catch { setMsg('Save failed'); }
    setBusy(false);
  };

  const place = async () => {
    const g = window.__placementGhost?.current;
    if (!g) { setMsg('Look at a wall first — no surface under the crosshair.'); return; }
    await create(g);
  };

  // Re-place the selected surface at the current crosshair (move/re-aim it).
  const moveSel = async () => {
    const g = window.__placementGhost?.current;
    if (!selected || !g) { setMsg('Look at a wall to move it there.'); return; }
    setBusy(true); setMsg('');
    try {
      // Same conversion as create — the crosshair is world-space wherever you
      // are standing, and a room's surface is stored in the room's space.
      const t = toRoomSpace(g);
      const { res, data } = await api.updatePlacement(selected, { pos: t.pos, quat: t.quat });
      if (res.ok && data.placement) { upsert(data.placement); setMsg(`Moved #${selected}`); }
      else setMsg(data.error || 'Move failed');
    } catch { setMsg('Move failed'); }
    setBusy(false);
  };

  // Push the panel's current size/aspect/style onto the selected surface.
  const applySel = async () => {
    if (!selected) return;
    setBusy(true); setMsg('');
    try {
      const { res, data } = await api.updatePlacement(selected, {
        width: draft.width, aspect: draft.aspect, surface_type: draft.surface_type,
        frame_style: draft.frame_style, traffic_tier: draft.traffic_tier,
        content_policy: draft.content_policy,
        playback: draft.playback || 'rotate',
        trigger_mode: draft.trigger_mode || 'proximity',
        volume: draft.volume ?? 1,
        trigger_radius: draft.trigger_radius ?? 8,
        // The nudged transform goes with it — otherwise Apply would save the
        // size you can see and silently discard the position you can also see.
        ...(draft.pos ? { pos: draft.pos } : {}),
        ...(draft.quat ? { quat: draft.quat } : {}),
        ...(draft.label ? { label: draft.label } : {}),
        ...(contentRef ? { default_content_ref: contentRef } : {}),
      });
      if (res.ok && data.placement) { upsert(data.placement); setMsg(`Updated #${selected}`); }
      else setMsg(data.error || 'Update failed');
    } catch { setMsg('Update failed'); }
    setBusy(false);
  };

  const duplicateSel = () => {
    const p = placements.find((x) => x.placement_id === selected);
    if (!p) return;
    setDraft({ aspect: p.aspect, surface_type: p.surface_type, frame_style: p.frame_style, traffic_tier: p.traffic_tier, width: p.width, label: p.label });
    setContentRef(p.default_content_ref || '');
    setMsg('Copied settings — look where you want it and Place.');
  };

  const deleteSel = async () => {
    if (!selected) return;
    setBusy(true);
    try { await api.deletePlacement(selected); removeP(selected); setSelected(null); setMsg('Deleted'); }
    catch { setMsg('Delete failed'); }
    setBusy(false);
  };

  // HIDE, WHICH IS NOT DELETE.
  //
  // Delete was the only way to clear a surface, and a placement is not cheap to
  // recreate: its transform was aimed by hand at a real wall, and a water
  // placement carries the whole footprint of a sea. Clearing a view to build
  // something else should not destroy that.
  //
  // `active` already exists on the table for this, the layer already renders
  // only active placements, and the spec's own rule is to soft-retire rather
  // than delete so history survives (§6). This just exposes what was already
  // there — the row keeps its geometry and comes back with one press.
  const hideSel = async () => {
    if (!selected) return;
    const p = placements.find((x) => x.placement_id === selected);
    const next = p?.active === false;   // currently hidden → bring it back
    setBusy(true);
    try {
      const saved = await api.updatePlacement(selected, { active: next });
      // Upserted, NOT removed from the list. Dropping it here would make a
      // hidden surface unselectable, which is the same as deleting it from
      // where anyone is standing.
      upsert(saved?.placement || { ...p, active: next });
      setMsg(next ? 'Shown' : 'Hidden — the row is kept, press again to bring it back');
    } catch { setMsg(next ? 'Show failed' : 'Hide failed'); }
    setBusy(false);
  };

  // Picking a podcast type sets the surface to PLAY by default, because that is
  // what somebody placing a television means (Khari: "Both of those should just
  // be 'on'"). Still a toggle — a rotating wall of episode covers is a fine
  // poster, it just is not the default reading of "interview podcast screen".
  const setType = (t) => setDraft({
    content_policy: [t.key], aspect: t.aspect,
    playback: PLAYABLE.includes(t.key) ? 'play' : 'rotate',
    ...(PLAYABLE.includes(t.key) ? { trigger_mode: 'proximity' } : {}),
  });
  // A campaign policy is `quickshout:<ref>`; the ref may be empty while it is
  // being typed, which is why the chip is lit by the PREFIX and not by the ref.
  const policy0 = draft.content_policy?.[0] || '';
  const isCampaign = policy0.startsWith('quickshout:');
  const campaignRef = isCampaign ? policy0.slice('quickshout:'.length) : '';

  const h = (draft.width / ({ '1:1': 1, '16:9': 16 / 9, '4:3': 4 / 3, '3:4': 3 / 4, '9:16': 9 / 16 }[draft.aspect] || 1)).toFixed(2);

  return (
    <div style={wrap} data-placement-panel>
      <AdminClose />
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', paddingRight: 24 }}>
        <b style={{ fontSize: 13 }}>Ad placements</b>
        <span style={{ fontSize: 11, opacity: 0.6 }}>{placements.length} placed</span>
      </div>
      {/* WHERE THIS ONE WILL LIVE. A surface belongs to exactly one scope and
          only renders there, so the difference between standing in a room and
          standing outside it is the difference between an ad in the salon and an
          ad floating over the city. Stated, not inferred. */}
      <div style={{
        marginTop: 6, padding: '5px 8px', borderRadius: 7, fontSize: 11,
        background: interior ? 'rgba(240,166,192,0.14)' : 'rgba(255,178,94,0.12)',
        color: interior ? '#f0a6c0' : '#ffb25e',
      }}>
        Placing in <b>{scopeLabel}</b>{interior ? ' — visible only in this room' : ' — visible outdoors'}
      </div>
      <div style={{ fontSize: 11, opacity: 0.6, marginTop: 3, lineHeight: 1.4 }}>
        {selected
          ? <>Editing <b>#{selected}</b> — change settings then <b>Apply</b>, or look elsewhere and <b>Move</b> it. Deselect to place a new one.</>
          : <>Look at a wall — the blue ghost snaps to it. <b>−/=</b> or scroll to resize · <b>Enter</b> to place. <b>Click an existing surface to edit it.</b></>}
      </div>

      {/* What house fill has to work with — a blank surface is almost always an
          empty pool (API unreachable) rather than a placement problem. */}
      <div style={{
        fontSize: 10.5, marginTop: 7, padding: '5px 7px', borderRadius: 7, lineHeight: 1.45,
        background: counts.ready && (counts.tracks + counts.artists + counts.videos) ? 'rgba(157,255,125,0.10)' : 'rgba(255,138,156,0.12)',
        color: counts.ready && (counts.tracks + counts.artists + counts.videos) ? '#9dff7d' : '#ff8a9c',
      }}>
        {!counts.ready ? 'House fill: loading…'
          : (counts.tracks + counts.artists + counts.videos) === 0
            ? 'House fill EMPTY — content API unreachable, surfaces stay blank.'
            : `House fill: ${counts.artists} artists · ${counts.tracks} tracks · ${counts.videos} videos`
              + ` · ${counts.interviews} interviews · ${counts.broadcasts} broadcasts`}

        {/* WHETHER THE CHOSEN TYPE CAN ACTUALLY SHOW ITSELF.
            An "Interview podcast" screen was quietly rendering music videos
            (Khari, 2026-08-04, salon). The placement was fine — the type simply
            had no pool, so house fill fell through to the next one and nothing
            said so. Falling back is still the right behaviour (a blank wall is
            worse), but it must be VISIBLE at the moment you pick the type,
            because on the wall it looks like the label is lying. */}
        {counts.ready && (() => {
          const t = draft.content_policy?.[0];
          const own = { interview_podcast: counts.interviews, news_broadcast: counts.broadcasts,
            featured_track: counts.tracks, featured_artist: counts.artists, hypeclip: counts.clips }[t];
          if (own === undefined || own > 0) return null;
          const why = t === 'interview_podcast'
            ? 'no published episodes with a guest and an image'
            : t === 'news_broadcast' ? 'no published banter episodes with an image'
              : t === 'hypeclip' ? 'no featured HypeClips' : 'that pool is empty';
          return (
            <div style={{ marginTop: 4, color: '#ffcf8a' }}>
              ⚠ {CONTENT_TYPES.find((c) => c.key === t)?.label}: {why} — this surface will show
              something else until there is one.
            </div>
          );
        })()}
        {selected && (() => {
          const p = placements.find((x) => x.placement_id === selected);
          if (!p) return null;
          const m = p.default_content_ref ? { still: p.default_content_ref, animated: null } : houseFillItem(p);
          return <div style={{ opacity: 0.85, marginTop: 3 }}>
            #{selected} → {p.default_content_ref ? 'set content' : (m ? 'house fill' : 'NOTHING')}
            {m?.animated ? ' (animated)' : ''}
          </div>;
        })()}
      </div>

      {/* ── THE ROOM'S OWN SURFACES ──────────────────────────────────────────
          Aiming at a wall works indoors exactly as it does outside, and it is the
          right tool for a room nobody designed for ads. But this room WAS
          designed for them: make_salon.mjs put the accent wall, the retail panel
          and the sign board where they are on purpose, and wrote their centre,
          facing and size into the model as affordances. Reading them back is one
          click for a dead-centre, correctly-sized ad — instead of re-deriving by
          hand what the builder already knew. */}
      {!selected && surfaces.length > 0 && (
        <>
          <div style={head}>Designed surfaces in this room</div>
          <div style={{ display: 'grid', gap: 4 }}>
            {surfaces.map((s) => (
              <button
                key={s.name}
                type="button"
                disabled={busy}
                // FIT BOTH DIMENSIONS. The server locks height to width/aspect, so
                // sizing to the surface's width alone would hang a 9:16 poster
                // through the floor on a wide wall. Whichever axis binds, wins.
                onClick={() => {
                  const ar = { '1:1': 1, '16:9': 16 / 9, '4:3': 4 / 3, '3:4': 3 / 4, '9:16': 9 / 16 }[draft.aspect] || 1;
                  const byH = s.surfaceSize ? s.surfaceSize[1] * 0.8 * ar : Infinity;
                  create({ ...s, width: Math.max(0.3, Math.min(s.width, byH)) }, s.name);
                }}
                style={{
                  display: 'flex', justifyContent: 'space-between', gap: 8, alignItems: 'center',
                  padding: '6px 9px', borderRadius: 8, cursor: busy ? 'default' : 'pointer',
                  background: 'rgba(240,166,192,0.10)', border: '1px solid rgba(240,166,192,0.4)',
                  color: '#ffe9c4', font: '600 11.5px system-ui', textAlign: 'left',
                }}
              >
                <span>{s.name.replace(/_/g, ' ')}</span>
                <span style={{ opacity: 0.6, fontWeight: 400 }}>
                  {s.surfaceSize ? `${s.surfaceSize[0].toFixed(1)}×${s.surfaceSize[1].toFixed(1)}u` : 'place'}
                </span>
              </button>
            ))}
          </div>
        </>
      )}

      <div style={head}>Content type</div>
      <div style={chipRow}>
        {CONTENT_TYPES.map((t) => (
          <button key={t.key} style={chip(draft.content_policy?.[0] === t.key)} onClick={() => setType(t)}>{t.label}</button>
        ))}
        {/* A BOOKED CAMPAIGN, not a house content kind — which is why it is a
            chip that reveals a field rather than another entry in the list. */}
        <button style={chip(isCampaign)} onClick={() => setDraft({ content_policy: [`quickshout:${campaignRef}`] })}>
          QuickShout ad
        </button>
      </div>

      {/* Khari, 2026-08-06: "we have a quickshout type, and an ID to enter. the
          ad placed will show and rotate the ads published by that ID."
          Stored INSIDE content_policy as `quickshout:<ref>` on purpose — that
          column is already a JSON array of strings, so a campaign needs no new
          column on `placements` and no migration on a table with live rows. */}
      {isCampaign && (
        <>
          <div style={head}>Advertiser ID</div>
          <input
            value={campaignRef}
            placeholder="the ID published from QuickShout"
            onChange={(e) => setDraft({ content_policy: [`quickshout:${e.target.value.trim()}`] })}
            style={{
              width: '100%', boxSizing: 'border-box', padding: '7px 9px', borderRadius: 8,
              fontSize: 12.5, border: '1px solid rgba(255,178,94,0.3)',
              background: 'rgba(255,255,255,0.06)', color: '#fff', outline: 'none',
            }}
          />
          <div style={{ fontSize: 11, opacity: 0.65, margin: '5px 0 0' }}>
            {campaignRef
              ? 'Rotates every creative published under this ID. Falls back to house content while it has none.'
              : 'Empty ID — this surface will show house content.'}
          </div>
        </>
      )}

      {/* ROTATE vs PLAY (migration 285). Offered only for the types that have an
          episode behind them, so the control never implies a featured track
          could "play". Rotation is what house fill has always done and is still
          the right behaviour for an advertising surface; a television is a
          different fixture, and this is where you say which one you placed. */}
      {PLAYABLE.includes(draft.content_policy?.[0]) && (
        <>
          <div style={head}>Behaviour</div>
          <div style={chipRow}>
            <button style={chip((draft.playback || 'rotate') !== 'play')} onClick={() => setDraft({ playback: 'rotate' })}>
              Rotate stills
            </button>
            <button style={chip(draft.playback === 'play')} onClick={() => setDraft({ playback: 'play' })}>
              Play episode
            </button>
          </div>
          <div style={{ fontSize: 10, opacity: 0.6, marginTop: 3, lineHeight: 1.4 }}>
            {draft.playback === 'play'
              ? 'Runs the latest episode — slides synced to the world clock. 🔊 on the screen mutes everything.'
              : 'Swaps episode covers every 45s, shared across the city.'}
          </div>

          {/* WHEN IT IS AUDIBLE, and HOW LOUD. Proximity is the default for a
              playing screen because hover is a mouse affordance — it does not
              exist on the phone build, in stereo/VR, or for a remote. */}
          {draft.playback === 'play' && (
            <>
              <div style={head}>Sound</div>
              <div style={chipRow}>
                {TRIGGERS.map((t) => (
                  <button key={t.key} style={chip((draft.trigger_mode || 'proximity') === t.key)}
                    onClick={() => setDraft({ trigger_mode: t.key })}>{t.label}</button>
                ))}
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 7, marginTop: 6 }}>
                <span style={{ fontSize: 10.5, opacity: 0.7, minWidth: 46 }}>
                  room {Math.round((draft.volume ?? 1) * 100)}%
                </span>
                <input
                  type="range" min="0" max="100" step="5"
                  value={Math.round((draft.volume ?? 1) * 100)}
                  onChange={(e) => setDraft({ volume: Number(e.target.value) / 100 })}
                  style={{ flex: 1, accentColor: '#ffb25e' }}
                />
              </div>
              {(draft.trigger_mode || 'proximity') === 'proximity' && (
                <div style={{ display: 'flex', alignItems: 'center', gap: 7, marginTop: 4 }}>
                  {/* QUARTER-METRE STEPS, AND A WAY TO HIT ONE EXACTLY.
                      Khari, 2026-08-04: "we need finer units on the floor
                      circumference size... 4.5 or 4.25 would be perfect for my
                      placement." A 1m step could not express either.

                      The nudges are not decoration: at 0.25 across a 2–40m
                      range the slider has ~150 positions on a ~120px track, so
                      dragging to an exact 4.25 is a pixel-hunt. Drag for the
                      neighbourhood, tap for the value — the same division of
                      labour the size control above already uses. */}
                  <span style={{ fontSize: 10.5, opacity: 0.7, minWidth: 46 }}>
                    {Number((draft.trigger_radius ?? 8).toFixed(2))}m
                  </span>
                  <button
                    style={{ ...chip(false), padding: '2px 7px', fontSize: 13, lineHeight: 1 }}
                    onClick={() => setDraft({ trigger_radius: Math.max(0.5, Number(((draft.trigger_radius ?? 8) - 0.25).toFixed(2))) })}
                  >−</button>
                  <input
                    type="range" min="0.5" max="40" step="0.25"
                    value={draft.trigger_radius ?? 8}
                    onChange={(e) => setDraft({ trigger_radius: Number(e.target.value) })}
                    style={{ flex: 1, accentColor: '#ffb25e' }}
                  />
                  <button
                    style={{ ...chip(false), padding: '2px 7px', fontSize: 13, lineHeight: 1 }}
                    onClick={() => setDraft({ trigger_radius: Math.min(40, Number(((draft.trigger_radius ?? 8) + 0.25).toFixed(2))) })}
                  >+</button>
                </div>
              )}
              <div style={{ fontSize: 10, opacity: 0.6, marginTop: 3, lineHeight: 1.4 }}>
                {(draft.trigger_mode || 'proximity') === 'always'
                  ? 'On all the time at the room level — a television murmuring in the corner.'
                  : (draft.trigger_mode === 'hover'
                    ? 'Only while pointed at. Mouse only — silent on phone and in VR.'
                    : 'Fades up as you walk in, out at the radius.')}
                {' '}<b>Standing at it lifts to 100%</b>, so walking closer is the volume
                control — no slider for the player. A scene can turn it up too. Loudest
                audible screen wins the mix.
              </div>
            </>
          )}
        </>
      )}

      <div style={head}>Size · {draft.width.toFixed(2)}m × {h}m</div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
        <button style={{ ...chip(false), padding: '5px 11px', fontSize: 15 }} onClick={() => resize(-0.5)}>−</button>
        <input
          type="range" min={0.3} max={30} step={0.1} value={draft.width}
          onChange={(e) => setDraft({ width: +e.target.value })}
          style={{ flex: 1, accentColor: '#ffb25e' }}
        />
        <button style={{ ...chip(false), padding: '5px 11px', fontSize: 15 }} onClick={() => resize(0.5)}>+</button>
      </div>
      <div style={{ fontSize: 10.5, opacity: 0.5, marginTop: 3 }}>scroll to resize · shift = fine</div>

      <div style={head}>Aspect</div>
      <div style={chipRow}>
        {ASPECTS.map((a) => <button key={a} style={chip(draft.aspect === a)} onClick={() => setDraft({ aspect: a })}>{a}</button>)}
      </div>

      <div style={head}>Surface</div>
      <div style={chipRow}>
        {SURFACES.map((s) => <button key={s} style={chip(draft.surface_type === s)} onClick={() => setDraft({ surface_type: s })}>{s.replace('_', ' ')}</button>)}
      </div>

      <div style={head}>Frame</div>
      <div style={chipRow}>
        {FRAMES.map((f) => <button key={f} style={chip(draft.frame_style === f)} onClick={() => setDraft({ frame_style: f })}>{f.replace('_', ' ')}</button>)}
      </div>

      <div style={head}>Traffic tier</div>
      <div style={chipRow}>
        {TIERS.map((t) => <button key={t} style={chip(draft.traffic_tier === t)} onClick={() => setDraft({ traffic_tier: t })}>{t}</button>)}
      </div>

      <div style={head}>Label</div>
      <input style={input} value={draft.label} placeholder="Broadway wall N" onChange={(e) => setDraft({ label: e.target.value })} />

      {/* Named for what it DOES. "House fill image URL" reads as "the image house
          fill should use", when it actually switches house fill OFF and pins this
          one image forever — which looks exactly like the rotation being broken. */}
      <div style={head}>Pinned image — overrides rotation (optional)</div>
      <input style={input} value={contentRef} placeholder="leave EMPTY to rotate house content" onChange={(e) => setContentRef(e.target.value)} />
      {contentRef && (
        <div style={{ fontSize: 10, color: '#ffb25e', opacity: 0.85, marginTop: 4, lineHeight: 1.35 }}>
          Pinned to one image — this surface will not rotate. Clear the field to let it cycle.
        </div>
      )}

      {/* NUDGE — move a placed surface without re-aiming at it. "Move here"
          re-raycasts from wherever you stand, which is fine for a wall you are
          square to and hopeless for anything you can only see obliquely (flat
          water most of all). These step it instead, relative to your view, and
          preview live; Apply saves the position along with the size. */}
      {selected && draft.pos && (
        <>
          <div style={head}>Nudge · hold Shift for fine</div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 5, marginTop: 6 }}>
            <span />
            <button style={{ ...chip(false), padding: '7px 0' }} onClick={(e) => nudge('depth', 1, e.shiftKey)}>↑</button>
            <span />
            <button style={{ ...chip(false), padding: '7px 0' }} onClick={(e) => nudge('across', -1, e.shiftKey)}>←</button>
            <div style={{ display: 'grid', placeItems: 'center', fontSize: 10, opacity: 0.6 }}>move</div>
            <button style={{ ...chip(false), padding: '7px 0' }} onClick={(e) => nudge('across', 1, e.shiftKey)}>→</button>
            <span />
            <button style={{ ...chip(false), padding: '7px 0' }} onClick={(e) => nudge('depth', -1, e.shiftKey)}>↓</button>
            <span />
          </div>
          <div style={{ display: 'flex', gap: 5, marginTop: 5 }}>
            <button style={{ ...chip(false), flex: 1 }} onClick={(e) => nudge('height', -1, e.shiftKey)}>▼ lower</button>
            <button style={{ ...chip(false), flex: 1 }} onClick={(e) => nudge('height', 1, e.shiftKey)}>raise ▲</button>
          </div>
          <div style={{ display: 'flex', gap: 5, marginTop: 5 }}>
            <button style={{ ...chip(false), flex: 1 }} onClick={(e) => turn(-1, e.shiftKey)}>⟲ turn</button>
            <button style={{ ...chip(false), flex: 1 }} onClick={(e) => turn(1, e.shiftKey)}>turn ⟳</button>
          </div>
          <div style={{ opacity: 0.5, fontSize: 10, marginTop: 6, fontVariantNumeric: 'tabular-nums' }}>
            {draft.pos.map((n) => n.toFixed(1)).join(', ')}
          </div>
        </>
      )}

      <div style={{ display: 'flex', gap: 6, marginTop: 12 }}>
        {selected ? (
          <>
            <button style={btn()} disabled={busy} onClick={moveSel}>{busy ? '…' : `Move #${selected} here`}</button>
            <button style={btn('rgba(255,178,94,0.16)', '#ffd27d')} disabled={busy} onClick={applySel}>Apply</button>
          </>
        ) : (
          <button style={btn()} disabled={busy} onClick={place}>{busy ? '…' : 'Place here'}</button>
        )}
      </div>

      {/* ── SCOPE REPAIR ──────────────────────────────────────────────────────
          Where a surface renders is decided ENTIRELY by its `zone`, and nothing
          about a surface looks wrong until you are standing in the place it is
          not. Any row made before scoping existed has zone NULL and therefore
          lives outdoors, wherever it was actually put — and there is no way to
          tell from inside the room, because from in there it is simply absent.
          So the scope is stated for the selected surface, and changing it is one
          press. The PATCH route merges over the current row, so this moves the
          scope and touches nothing else. */}
      {selected && (() => {
        const p = placements.find((x) => x.placement_id === selected);
        if (!p) return null;
        const zone = p.zone || null;
        const here = interior || null;
        const wrong = zone !== here;
        return (
          <div style={{
            marginTop: 8, padding: '6px 8px', borderRadius: 7, fontSize: 11, lineHeight: 1.45,
            background: wrong ? 'rgba(255,138,156,0.12)' : 'rgba(157,255,125,0.10)',
            color: wrong ? '#ff8a9c' : '#9dff7d',
          }}>
            #{selected} lives in <b>{zone ? (ROOMS[zone]?.name || zone) : 'the street'}</b>
            {wrong && (
              <button
                type="button"
                disabled={busy}
                onClick={async () => {
                  setBusy(true); setMsg('');
                  try {
                    const { res, data } = await api.updatePlacement(selected, { zone: here });
                    if (res.ok && data.placement) { upsert(data.placement); setMsg(`#${selected} → ${here || 'street'}`); }
                    else setMsg(data.error || 'Move failed');
                  } catch { setMsg('Move failed'); }
                  setBusy(false);
                }}
                style={{
                  display: 'block', marginTop: 5, width: '100%', padding: '5px 8px', borderRadius: 6,
                  cursor: 'pointer', font: '600 11.5px system-ui', color: '#ffe9c4',
                  background: 'rgba(255,178,94,0.16)', border: '1px solid rgba(255,178,94,0.5)',
                }}
              >
                Move it into {here ? (ROOMS[here]?.name || here) : 'the street'}
              </button>
            )}
          </div>
        );
      })()}

      {msg && <div style={{ fontSize: 11.5, marginTop: 7, color: msg.includes('fail') ? '#ff8a9c' : '#9dff7d' }}>{msg}</div>}

      <div style={head}>Placed surfaces</div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
        {placements.length === 0 && <div style={{ fontSize: 11.5, opacity: 0.5 }}>None yet.</div>}
        {placements.map((p) => (
          <button
            key={p.placement_id}
            onClick={() => setSelected(selected === p.placement_id ? null : p.placement_id)}
            style={{
              textAlign: 'left', padding: '6px 8px', borderRadius: 7, cursor: 'pointer', font: '12px system-ui',
              border: `1px solid ${selected === p.placement_id ? '#9dff7d' : 'rgba(255,255,255,0.12)'}`,
              background: selected === p.placement_id ? 'rgba(157,255,125,0.12)' : 'transparent', color: '#ffe9c4',
            }}
          >
            #{p.placement_id} · {p.label || p.surface_type} <span style={{ opacity: 0.55 }}>({p.aspect}, {p.width.toFixed(1)}m)</span>
          </button>
        ))}
      </div>
      {selected && (
        <div style={{ display: 'flex', gap: 6, marginTop: 8 }}>
          <button style={btn('transparent', '#ffe9c4')} onClick={() => setSelected(null)}>Deselect</button>
          <button style={btn('transparent', '#ffd27d')} onClick={duplicateSel}>Duplicate</button>
          <button style={btn('transparent', '#7dd3ff')} disabled={busy} onClick={hideSel}>
            {placements.find((x) => x.placement_id === selected)?.active === false ? 'Show' : 'Hide'}
          </button>
          <button style={btn('transparent', '#ff8a9c')} disabled={busy} onClick={deleteSel}>Delete</button>
        </div>
      )}
    </div>
  );
}
